import { CircleNotchIcon } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import type { ReactNode } from "react";
import { Link } from "react-router";

import type { UpgradeInterval, UpgradeTier } from "@/client/hooks/use-upgrade-checkout";
import { useUpgradeCheckout } from "@/client/hooks/use-upgrade-checkout";
import type { UpgradePromptSurface } from "@/client/hooks/use-upgrade-prompt-viewed";
import { useUpgradePromptViewed } from "@/client/hooks/use-upgrade-prompt-viewed";
import { useUser } from "@/client/services/user";

type ButtonVariant = React.ComponentProps<typeof Button>["variant"];
type ButtonSize = React.ComponentProps<typeof Button>["size"];

/**
 * Which plan to sell when the call site does not name one.
 *
 * `null` means "nothing left to sell" and the control renders nothing.
 *
 * This must never resolve to the plan the user already owns. The billing
 * webhook upserts `Subscription` by `userId` and overwrites both `tier` and
 * `lsSubscriptionId` (`billing.service.ts`), so a second checkout does not add
 * a plan — it REPLACES the tracked one. Selling PRO to an ULTIMATE subscriber
 * therefore double-bills them in Lemon Squeezy *and* downgrades them here,
 * orphaning the original subscription id so its later webhooks match nothing.
 *
 * A TRIALING user is on the PRO trial tier but has not bought anything yet, so
 * they are still sold PRO — that is what "Keep Pro" in the trial banner means.
 */
const defaultTierFor = (
  tier: string | undefined,
  status: string | undefined,
): UpgradeTier | null => {
  if (!tier || status === "TRIALING") return "PRO";
  if (tier === "ULTIMATE") return null;
  return tier === "PRO" ? "ULTIMATE" : "PRO";
};

/**
 * Which billing cycle to open when the call site does not name one.
 *
 * Pro defaults to WEEKLY so every in-app upgrade matches /pricing, which
 * leads with the $7.99 week — the low-commitment ask the revenue plans chose
 * for trialists (founder decision, 2026-10-09). Ultimate has no weekly
 * variant, so it stays monthly.
 */
export const defaultIntervalFor = (tier: UpgradeTier | null): UpgradeInterval =>
  tier === "PRO" ? "weekly" : "monthly";

export type UpgradeButtonProps = {
  /**
   * Which plan the checkout opens. Omit to derive it from the signed-in user's
   * current subscription — see `defaultTierFor`. Pass it explicitly only when
   * the surface is tied to one specific plan (e.g. the Ultimate-only stats
   * paywall), and gate the call site yourself when you do.
   */
  tier?: UpgradeTier;
  /** Billing cycle. Defaults to `defaultIntervalFor(tier)` — weekly for Pro. */
  interval?: UpgradeInterval;
  /** Attribution label for the /pricing fallback UTM, e.g. "sidebar". */
  source: string;
  /** Defaults to the full hosted page; the LS overlay 403s when third-party cookies are blocked. */
  mode?: "overlay" | "hosted";
  /** Localised label — wrap in `<Trans>` at the call site. */
  children: ReactNode;
  /** Localised accessible name — build with the `t` macro at the call site. */
  ariaLabel?: string;
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Render a bare anchor carrying `className` instead of a `<Button>` shell,
   * for inline text CTAs (the sidebar usage meter, the daily-cap banner)
   * where a filled button would break the row layout.
   */
  unstyled?: boolean;
  /** Extra side effect on click (local analytics, closing a menu, …). */
  onOpen?: () => void;
  /**
   * While `/billing/config` is still loading, render a disabled, busy button
   * instead of the `/pricing` link.
   *
   * Set it on surfaces that exist to RESUME a checkout the buyer already
   * chose (the checkout-resume card): a click that lands on `/pricing` there
   * drops the plan and cycle they picked and never reaches
   * `POST /billing/checkouts`. The `/pricing` link remains the fallback for
   * `unavailable` (billing off or the request failed), where waiting would
   * never end, and for every control that does not set this.
   */
  holdWhileLoading?: boolean;
  /**
   * Report `upgrade_prompt_viewed` with this surface when the control mounts. Set it on
   * a surface that shows the offer without any view event of its own. Leave it off where
   * the host already reports a view (a dialog's `paywall_viewed`) so one offer is not
   * counted twice. The server keeps one row per user, surface and UTC day.
   */
  viewSurface?: UpgradePromptSurface;
};

/**
 * The single in-app control that opens a Lemon Squeezy checkout.
 *
 * For `interval: "monthly"` it renders a REAL anchor with a REAL `href` and
 * only `preventDefault()` + `openCheckout()` on click. That is deliberate
 * progressive enhancement, not an oversight: middle-click and open-in-new-tab
 * keep working, and if lemon.js never loads the click falls through to the
 * hosted checkout page. Monthly is safe for this because it is also what
 * Lemon Squeezy's hosted checkout defaults to on its own.
 *
 * For `weekly`/`yearly` it renders a plain `<button>` with NO `href` at all
 * (Codex review, 2026-09-21 — BLOCKER A). The static checkout URL for those
 * intervals pre-selects Monthly on Lemon Squeezy's page — the exact defect
 * this whole flow exists to fix — so there is no href a middle-click could
 * safely reach directly. `openCheckout` (`useUpgradeCheckout`) POSTs
 * `/billing/checkouts` and opens the API-bound URL; on failure it shows an
 * error toast rather than falling back to that unsafe static URL, and the
 * button re-enables so the buyer can retry.
 *
 * When no checkout URL resolves — billing unconfigured, the request still in
 * flight, or a cached anonymous config with no `checkout[custom][user_id]` —
 * it degrades to a `/pricing` link rather than becoming a dead click.
 * `data-checkout-state` exposes which of these it is, so tests (and support)
 * can tell "the button did nothing" from "the button linked to pricing on
 * purpose".
 */
export const UpgradeButton = ({
  tier,
  interval,
  source,
  mode,
  children,
  ariaLabel,
  className,
  variant,
  size,
  unstyled = false,
  onOpen,
  holdWhileLoading = false,
  viewSurface,
}: UpgradeButtonProps) => {
  const { user } = useUser();
  const derivedTier = defaultTierFor(user?.subscription?.tier, user?.subscription?.status);
  const resolvedTier = tier ?? derivedTier;
  const resolvedInterval = interval ?? defaultIntervalFor(resolvedTier);

  const { state, checkoutUrl, pricingHref, openCheckout, isCheckingOut } = useUpgradeCheckout({
    // Hooks run unconditionally; the `null` case bails out after this call.
    tier: resolvedTier ?? "PRO",
    interval: resolvedInterval,
    source,
    mode,
  });

  // Nothing renders when there is no plan to sell, so nothing was viewed either.
  useUpgradePromptViewed(viewSurface, resolvedTier !== null);

  // Already on the top plan and the call site named no tier: there is nothing
  // to sell, and every alternative (a PRO checkout, a /pricing link) is worse
  // than showing no control at all.
  if (!resolvedTier) return null;

  // When wrapped in `<Button asChild>` the Slot merges the button classes onto
  // this child, so the child must not carry `className` itself.
  const shared = {
    "aria-label": ariaLabel,
    "aria-busy": state === "loading" || isCheckingOut,
    "data-checkout-state": state,
    "data-checkout-tier": resolvedTier,
    "data-checkout-interval": resolvedInterval,
    className: unstyled ? className : undefined,
  };

  // Monthly's static href is a safe middle-click fallback; weekly/yearly's is
  // not (it pre-selects Monthly on LS's page — see the doc comment above), so
  // those render a plain button with no href to reach directly.
  const isStaticHrefSafe = resolvedInterval === "monthly";

  let control: ReactNode;
  if (!checkoutUrl && holdWhileLoading && state === "loading") {
    control = (
      <button {...shared} disabled type="button">
        <CircleNotchIcon className="mr-2 animate-spin" aria-hidden="true" />
        {children}
      </button>
    );
  } else if (!checkoutUrl) {
    control = (
      <Link {...shared} to={pricingHref} onClick={onOpen}>
        {children}
      </Link>
    );
  } else if (isStaticHrefSafe) {
    control = (
      <a
        {...shared}
        href={checkoutUrl}
        aria-disabled={isCheckingOut}
        onClick={(event) => {
          event.preventDefault();
          onOpen?.();
          openCheckout();
        }}
      >
        {children}
      </a>
    );
  } else {
    control = (
      <button
        {...shared}
        type="button"
        disabled={isCheckingOut}
        onClick={() => {
          onOpen?.();
          openCheckout();
        }}
      >
        {children}
      </button>
    );
  }

  if (unstyled) return control;

  return (
    <Button asChild variant={variant} size={size} className={className}>
      {control}
    </Button>
  );
};
